import { CircularProgress, Table, TableHead, TableBody, TableCell, Typography, makeStyles, Grid, TableRow, Checkbox, Select, MenuItem, FormControl } from '@material-ui/core';
import { getUsers, updateUser } from '../../api/user';
import { User, UserRole } from '../../domain/domain';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../reducers';
import { compose } from 'redux';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Link, useHistory } from 'react-router-dom';
import { useErrorNotification } from '../../contexts/ErrorNotificationContext';


const useStyles = makeStyles((_) => ({
  root: {
    maxWidth: "80%",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 20,
  },
  columnTitle: {
    fontWeight: "bold",
  },
  select: {
    width: 200,
  },
}));

type Props = {
    currentUser: User
}

const UserManagementPage: React.FC<Props> = ({ currentUser }) => {
  const classes = useStyles();
  const history = useHistory();
  const { showError } = useErrorNotification();
  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const userRoles:Record<UserRole, string> = {
    [UserRole.NON_PUBLICATIONS_TEAM]: "Non-Publications Team",
    [UserRole.PUBLICATIONS_TEAM]: "Publications Team",
    [UserRole.ADMINISTRATOR]: "Publications Administrator",
  }

  useDocumentTitle("User Management");

  useEffect(() => {
    if (currentUser.userRole !== UserRole.ADMINISTRATOR) {
        showError(
            'Access Denied',
            'You do not have permission to access this page.',
            'Only administrators can access this page.'
        );
        return;
    }
    getUsers()
      .then((response) => {
        setUsers(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [currentUser.userRole, showError, history]);

  if (!users) {
    return <CircularProgress />;
  }
  return (
    <Grid
      container
      direction="column"
      className={classes.root}
      alignItems="center"
      spacing={4}
    >
      <Grid item>
        <Link to="/admin">Administrator Page</Link>
      </Grid>
      <Grid item>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Username
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Role
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Email
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Registered
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Last Login
                </Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.columnTitle}>
                  Active
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => {
              return (
                <TableRow key={u.id}>
                  <TableCell>
                    <Typography>{u.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <Select
                        value={u.userRole}
                        variant="outlined"
                        disabled={u.id === currentUser.id}
                        className={classes.select}
                        title={`Role ${u.email}`}
                        inputProps={{
                          "aria-label": `Role ${userRoles[u.userRole]} ${u.email}`,
                        }}
                        onChange={event => {
                          u.userRole = event.target.value as UserRole;
                          updateUser(u)
                            .then((response) => {
                              let idx = users.findIndex(
                                u => u.id === response.data.id
                              );
                              if (idx >= 0) {
                                let newUsers = [...users];
                                newUsers[idx] = response.data;
                                setUsers(newUsers);
                              }
                            })
                            .catch((error) => {
                              console.error(error);
                            });
                        }}
                      >
                        <MenuItem value={UserRole.NON_PUBLICATIONS_TEAM}>
                          {userRoles[UserRole.NON_PUBLICATIONS_TEAM]}
                        </MenuItem>
                        <MenuItem value={UserRole.PUBLICATIONS_TEAM}>
                          {userRoles[UserRole.PUBLICATIONS_TEAM]}
                        </MenuItem>
                        <MenuItem value={UserRole.ADMINISTRATOR}>
                          {userRoles[UserRole.ADMINISTRATOR]}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography>{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {u.registeredDate
                        ? moment(u.registeredDate).format("DD.MM.YYYY HH:mm:ss")
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {u.lastLoginDate
                        ? moment(u.lastLoginDate).format("DD.MM.YYYY HH:mm:ss")
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={u.userActive}
                      disabled={u.id === currentUser.id}
                      inputProps={{
                        "aria-label": "Active",
                        "title": "Active",
                      }}
                      onChange={(event) => {
                        u.userActive = event.target.checked;
                        updateUser(u)
                          .then((response) => {
                            let idx = users.findIndex(
                              (u) => u.id === response.data.id
                            );
                            if (idx >= 0) {
                              let newUsers = [...users];
                              newUsers[idx] = response.data;
                              setUsers(newUsers);
                            }
                          })
                          .catch((error) => {
                            console.error(error);
                          });
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: ApplicationState) => {
  return {
    currentUser: state.user.currentUser,
  };
};

export default compose<any>(connect(mapStateToProps, null))(UserManagementPage);
